import { Trans } from 'react-i18next';

import {
   HealthcareProviderAppointmentCancellationPractitionerNotificationConfigurationQuery,
   HealthcareProviderAppointmentCancellationPractitionerNotificationEnabledSetCommand,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   Form,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';
import { PractitionerPhoneNotificationAppointmentCancellationToggleLoader } from './PractitionerPhoneNotificationAppointmentCancellationToggle.loader';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;

const useCurrentHealthcareProviderAppointmentCancellationPractitionerNotificationEnabledSetCommand = (): UseCqrsCommandResult<
   void,
   PractitionerPhoneNotificationAppointmentCancellationToggleFormBean
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentCancellationPractitionerNotificationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (ignore, parameters) => {
            dispatchSnackbarPositiveEvent(
               parameters.notificationEnabled
                  ? patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentPractitionerCancellationToggleSaveOn
                  : patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentPractitionerCancellationToggleSaveOff,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            refetchCqrsQuery(HealthcareProviderAppointmentCancellationPractitionerNotificationConfigurationQuery);
         },
      }
   );
};

type PractitionerPhoneNotificationAppointmentCancellationToggleFormBean = {
   notificationEnabled: boolean;
};

export const PractitionerPhoneNotificationAppointmentCancellationToggle = (props: {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
}): JSX.Element | null => {
   const {
      register,
      handleSubmit,
   } = useFormController<PractitionerPhoneNotificationAppointmentCancellationToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );
   const {
      executeAsync: enablePractitionerNotificationSet,
      loading,
   } = useCurrentHealthcareProviderAppointmentCancellationPractitionerNotificationEnabledSetCommand();

   return (
      <StyledFlex gap={'12px'}>
         <Form onInput={handleSubmit(enablePractitionerNotificationSet)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'practitionerPhoneNotificationAppointmentCancellationToggle'}
               disabled={loading}
            />
         </Form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans
               i18nKey={
                  patientPhoneNotificationTranslationKeys.notificationToggle.appointmentPractitionerCancellationToggle
               }
            />
            <Tooltip
               maxWidth={'600px'}
               testId={'appointmentPractitionerCancellationToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle
                           .appointmentPractitionerCancellationToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'appointmentPractitionerCancellationToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PractitionerPhoneNotificationAppointmentCancellationToggleWithLoader = (): JSX.Element => {
   return (
      <PractitionerPhoneNotificationAppointmentCancellationToggleLoader>
         {({ healthcareProviderNotificationConfiguration }) => (
            <PractitionerPhoneNotificationAppointmentCancellationToggle
               notificationEnabled={healthcareProviderNotificationConfiguration.enabled}
               messages={healthcareProviderNotificationConfiguration.messages}
            />
         )}
      </PractitionerPhoneNotificationAppointmentCancellationToggleLoader>
   );
};
